import type { Coding } from '@medplum/fhirtypes'
import type { CodingValue } from '@seen/core/codeableConcept'
import * as codeSystems from '@seen/core/codeSystems'
import { formatDateNumberShort, formatDateTime } from '@seen/core/dates'
import { formatObservationValueDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import {
  GENERAL_OBSERVATIONS,
  type GeneralObservation,
  generalObservationCoding,
  isGeneralObservationCoding,
} from '@seen/core/models/generalObservations'
import { ExtendedObservation } from '@seen/core/models/observation'
import { groupObservationCaptures } from '@seen/core/models/observationCaptures'
import {
  Participant,
  type ParticipantReferenceString,
} from '@seen/core/models/patients/participant'
import { RelevantObservations } from '@seen/core/models/relevantObservations'
import {
  Button,
  DotSeparator,
  HOVER_CARD_SIDE_OFFSET,
  ReorderList,
} from '@seen/ui'
import {
  IconActivity,
  IconFlask,
  IconInfoCircle,
  IconPlus,
  IconX,
} from '@tabler/icons-react'
import { orderBy, uniqBy } from 'lodash-es'
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ComboboxAsync,
  type ComboboxAsyncOption,
} from '#src/components/core/combobox/ComboboxAsync.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { ObservationHistoryChart } from '#src/components/observation/ObservationHistoryChart.tsx'
import {
  FacesheetCard,
  FacesheetCardActions,
  FacesheetCardAddButton,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
  type FacesheetObservationSection,
  reportFacesheetObservationPinAdded,
  reportFacesheetObservationPinRemoved,
  reportFacesheetObservationPinsReordered,
  reportFacesheetObservationsSeeAllClicked,
  reportFacesheetObservationsTitleClicked,
  reportFacesheetObservationTrendOpened,
  useFacesheetTab,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import {
  FACESHEET_CARD_LIST_CLASS,
  FACESHEET_CARD_ROW_CLASS,
  FacesheetCardSection,
  FacesheetCardSections,
} from '#src/components/patient/detail-content/FacesheetCardSection.tsx'
import {
  loadObservationOptions,
  observationCodeOption,
} from '#src/components/rich-text-editor/mention/observationOptions.ts'
import {
  defaultToOption,
  type ValueSetAutocompleteValue,
} from '#src/components/ValueSetAutocomplete/toOptions.ts'
import { valueSetAutocompleteLoadOptions } from '#src/components/ValueSetAutocomplete/valueSetAutocompleteLoadOptions.ts'
import {
  OBSERVATION_HOVER_CARD_WIDTH,
  useAdaptiveHoverCardPlacement,
} from '#src/hooks/useAdaptiveHoverCardPlacement.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useFhirUpsertMutation } from '#src/hooks/useFhirUpsertMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useOptimisticPatchMutation } from '#src/hooks/useOptimisticPatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchCount } from '#src/hooks/useSearchCount.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useSearchResourcePagesUntil } from '#src/hooks/useSearchResourcePagesUntil.ts'

const GENERAL_CODINGS = GENERAL_OBSERVATIONS.flatMap((item) => item.codings)

const RECENT_LIMIT = 5
// Bounds the facesheet's cost for a participant whose history is mostly one
// code, at the price of fewer Recent rows.
const RECENT_MAX_PAGES = 10

function recentObservationRows(
  observations: ExtendedObservation[],
  structuredCapture: boolean
): ExtendedObservation[] {
  return uniqBy(
    observations.filter(
      (obs) =>
        !(structuredCapture && obs.isPanel) &&
        !obs.codeableConcept.coding?.some(isGeneralObservationCoding) &&
        // Panels with no value of their own (their results are separate
        // observations) would read as an empty row.
        !!formatObservationValueDisplay(obs.toFhirResource())
    ),
    (obs) => {
      const coding = obs.codeableConcept.coding?.[0]
      return coding ? `${coding.system}|${coding.code}` : obs.display
    }
  )
}

export function RelevantParticipantObservations({
  participantReferenceString,
  variant = 'for-you',
}: {
  participantReferenceString: ParticipantReferenceString
  // For you: General plus the practitioner's own pins, editable. Highlights:
  // General plus the participant's recent observations, read-only. Ignored
  // with facesheet-improvements off, where every card shows the practitioner's
  // pins.
  variant?: 'for-you' | 'highlights'
}): ReactNode {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const medplum = useMedplum()
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )

  const showRecent = facesheetImprovements && variant === 'highlights'
  const showPins = !showRecent

  const { data: participant } = useRead(Participant, participantReferenceString)
  // Our own question codes can be added, and panels (records that only group
  // their members) are left out.
  const structuredCapture = useFeatureFlag(featureFlags.structuredCapture)

  const { data: relevantObservations } = useSearchOne(
    RelevantObservations,
    showPins
      ? RelevantObservations.bySource(practitioner?.reference)
      : undefined
  )
  const upsert = useFhirUpsertMutation('List')
  const patch = useOptimisticPatchMutation<RelevantObservations>()

  // The drop handler sets this synchronously, so the first frame after a drop
  // already has the new order. The optimistic cache write reaches components a
  // tick later, and dnd-kit animates the rows from the old order into the new
  // one during that gap.
  const [droppedOrder, setDroppedOrder] = useState<string[] | null>(null)
  useEffect(() => {
    setDroppedOrder(null)
  }, [relevantObservations])

  // Pins chosen while the practitioner's first list is being created. A
  // second create would replace the first list, so choices made before it
  // returns are patched onto the saved list instead. The ref is read when the
  // create returns, after the render that captured the state.
  const firstPinsRef = useRef<CodingValue[] | null>(null)
  const [firstPins, setFirstPins] = useState<CodingValue[] | null>(null)
  const updateFirstPins = (codings: CodingValue[] | null): void => {
    firstPinsRef.current = codings
    setFirstPins(codings)
  }
  useEffect(() => {
    if (relevantObservations) {
      firstPinsRef.current = null
      setFirstPins(null)
    }
  }, [relevantObservations])

  const { data: searchedObservations } = useSearch(
    ExtendedObservation,
    showPins
      ? {
          _sort: '-date',
          subject: participant?.reference,
          'status:not': 'entered-in-error',
          _count: 50,
        }
      : undefined
  )

  const recentObservations = useMemo(
    () =>
      structuredCapture
        ? searchedObservations?.filter((obs) => !obs.isPanel)
        : searchedObservations,
    [searchedObservations, structuredCapture]
  )

  // The server leaves out General codes, so General vitals can't crowd the
  // other observations out of the page. One code repeated many times still
  // can, so pages are read until there are enough distinct rows.
  const { data: recentNonGeneral } = useSearchResourcePagesUntil(
    ExtendedObservation,
    showRecent && participant
      ? {
          _sort: '-date',
          subject: participant.reference,
          'status:not': 'entered-in-error',
          'code:not': GENERAL_CODINGS,
          _count: 100,
        }
      : undefined,
    {
      enough: (observations) =>
        recentObservationRows(observations, structuredCapture).length >=
        RECENT_LIMIT,
      maxPages: RECENT_MAX_PAGES,
    }
  )
  const recentRows = useMemo(
    () => recentObservationRows(recentNonGeneral ?? [], structuredCapture),
    [recentNonGeneral, structuredCapture]
  )
  const observationCount = useSearchCount(
    'Observation',
    showRecent && participant
      ? ExtendedObservation.participantObservationsQuery(participant.reference)
      : undefined
  )

  const localOptions =
    useMemo((): ComboboxAsyncOption<ValueSetAutocompleteValue>[] => {
      if (!recentObservations?.length) {
        return []
      }

      const observations = recentObservations
        .map((obs) => {
          const coding = obs.codeableConcept.coding?.[0]
          if (!coding?.code || !coding?.display || !coding?.system) {
            return null
          }
          return {
            code: coding.code,
            display: coding.display,
            system: coding.system,
          }
        })
        .filter((v): v is ValueSetAutocompleteValue => v !== null)
        .filter(
          (coding) =>
            !facesheetImprovements || !isGeneralObservationCoding(coding)
        )

      // Get unique by code
      const uniqueObservations = uniqBy(observations, (obs) => obs.code)

      // With structured capture the rows say how often each code is used.
      return uniqueObservations.map(
        structuredCapture ? observationCodeOption : defaultToOption
      )
    }, [recentObservations, facesheetImprovements, structuredCapture])

  const loadOptions = useCallback(
    async ({ input, signal }: { input: string; signal: AbortSignal }) => {
      if (structuredCapture) {
        return loadObservationOptions({
          input,
          signal,
          medplum,
          includeLibrary: true,
          ranked: true,
        })
      }
      const options = await valueSetAutocompleteLoadOptions({
        input,
        signal,
        medplum,
        expandParams: {},
        binding: codeSystems.LOINC_OBSERVATIONS_VALUE_SET,
        toOption: defaultToOption,
      })
      return facesheetImprovements
        ? options.filter((option) => !isGeneralObservationCoding(option.value))
        : options
    },
    [medplum, facesheetImprovements, structuredCapture]
  )

  const fallbackObservations = useMemo(() => {
    if (facesheetImprovements || !recentObservations?.length) {
      return []
    }

    // Get selected codes
    const selectedCodes = new Set(
      relevantObservations?.values?.map((v) => v.code) ?? []
    )

    // Filter out selected codes and deduplicate by code
    const uniqueObservations = uniqBy(
      recentObservations.filter((obs) => {
        return !selectedCodes.has(obs.code)
      }),
      (obs) => obs.code
    )

    return uniqueObservations.slice(0, 5)
  }, [relevantObservations?.values, recentObservations, facesheetImprovements])

  if (!participant) {
    return null
  }

  const pinnedObservationTypes = droppedOrder
    ? orderBy(
        relevantObservations?.values ?? [],
        (observationType) => {
          const index = droppedOrder.indexOf(reorderId(observationType))
          return index === -1 ? Infinity : index
        },
        'asc'
      )
    : (relevantObservations?.values ?? firstPins ?? [])
  // A pin that duplicates a General row stays in the practitioner's list but
  // is not shown twice.
  const additionalObservationTypes = facesheetImprovements
    ? pinnedObservationTypes.filter(
        (observationType) => !isGeneralObservationCoding(observationType)
      )
    : pinnedObservationTypes

  const hasObservations =
    facesheetImprovements ||
    pinnedObservationTypes.length > 0 ||
    fallbackObservations.length > 0

  const handleAddObservation = (
    coding: { code: string; display: string; system: string } | null
  ): void => {
    if (!coding) {
      return
    }

    if (
      facesheetImprovements &&
      additionalObservationTypes.some(
        (pinned) =>
          pinned.system === coding.system && pinned.code === coding.code
      )
    ) {
      handleRemoveObservation(coding, 'menu')
      return
    }

    if (facesheetImprovements) {
      reportFacesheetObservationPinAdded({
        code: coding.code,
        system: coding.system,
        pinCount: additionalObservationTypes.length + 1,
      })
    }

    if (!relevantObservations) {
      if (firstPinsRef.current) {
        updateFirstPins([coding, ...firstPinsRef.current])
        return
      }
      updateFirstPins([coding])

      const updatedObservations = new RelevantObservations({
        source: practitioner?.reference,
      })
      updatedObservations.add(coding)

      upsert.mutate(
        {
          resource: updatedObservations.toFhirResource(),
          query: RelevantObservations.bySource(practitioner?.reference),
        },
        {
          onSuccess: (saved) => {
            const wanted = firstPinsRef.current ?? []
            if (wanted.length === 1 && sameCoding(wanted[0], coding)) {
              return
            }
            patch.mutate({
              instance: RelevantObservations.fromFhirResource(saved),
              mutateFn: (ro) => {
                // `add` puts a pin first, so the oldest choice goes in first.
                ro.remove(coding)
                for (const pin of [...wanted].reverse()) {
                  ro.add(pin)
                }
              },
              errorMessage: "Couldn't add the observation",
            })
          },
          onError: () => updateFirstPins(null),
        }
      )
      return
    }

    patch.mutate({
      instance: relevantObservations,
      mutateFn: (ro) => {
        ro.add(coding)
      },
      errorMessage: "Couldn't add the observation",
    })
  }

  const handleRemoveObservation = (
    coding: {
      code?: string
      display?: string
      system?: string
    },
    via: 'menu' | 'row'
  ): void => {
    const pendingFirstPins = firstPinsRef.current
    if (!relevantObservations && pendingFirstPins) {
      updateFirstPins(
        pendingFirstPins.filter((pin) => !sameCoding(pin, coding))
      )
    }
    if (!relevantObservations || !coding.code) {
      return
    }

    if (facesheetImprovements) {
      reportFacesheetObservationPinRemoved({
        code: coding.code,
        system: coding.system ?? '',
        pinCount: additionalObservationTypes.length - 1,
        via,
      })
    }

    patch.mutate({
      instance: relevantObservations,
      mutateFn: (ro) => {
        ro.remove(coding as CodingValue)
      },
      errorMessage: "Couldn't remove the observation",
    })
  }

  const handleReorder = (orderedIds: string[]): void => {
    if (!relevantObservations) {
      return
    }
    setDroppedOrder(orderedIds)

    const idToObservationType = new Map(
      relevantObservations.values.map((observationType) => [
        reorderId(observationType),
        observationType,
      ])
    )

    const orderedObservationTypes = orderedIds.flatMap((id) => {
      const observationType = idToObservationType.get(id)
      return observationType !== undefined ? [observationType] : []
    })

    if (facesheetImprovements) {
      reportFacesheetObservationPinsReordered({
        pinCount: additionalObservationTypes.length,
      })
    }

    patch.mutate({
      instance: relevantObservations,
      mutateFn: (ro) => {
        ro.setOrder(orderedObservationTypes)
      },
      errorMessage: "Couldn't save the new observation order",
    })
  }

  return (
    <FacesheetCard
      className="group"
      collapsible={hasObservations}
      rumName="observations"
    >
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon>
            {facesheetImprovements ? <IconActivity /> : <IconFlask />}
          </FacesheetCardIcon>
          <FacesheetCardLabel>
            {facesheetImprovements ? (
              <TextLink
                to={`${participant.relativeUrl}/observations`}
                onClick={() =>
                  reportFacesheetObservationsTitleClicked({
                    participantId: participant.id,
                    tab: variant,
                  })
                }
              >
                Observations
              </TextLink>
            ) : (
              'Observations'
            )}
          </FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
          {/* The design has no info icon; the x tooltip says what a pin
              does. */}
          {!facesheetImprovements && (
            <Tooltip>
              <TooltipTrigger>
                <IconInfoCircle className="size-3 opacity-0 group-hover:opacity-100" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Personalized list of observations that are relevant for you to
                  track across all participants. Additionally, recent
                  observations for this participant are shown below.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </FacesheetCardTitle>
        <FacesheetCardActions>
          {!isReadOnly && showPins && (
            <ComboboxAsync
              queryKey={[
                'observation-autocomplete',
                participant.reference.reference,
                structuredCapture ? 'ranked' : 'loinc',
              ]}
              loadOptions={loadOptions}
              sourceRanked={structuredCapture}
              className={
                structuredCapture
                  ? 'w-[min(36rem,calc(100vw-2rem))]'
                  : undefined
              }
              onSelect={handleAddObservation}
              getValue={(o) => o.code ?? ''}
              localOptions={localOptions}
              selected={
                facesheetImprovements
                  ? additionalObservationTypes.flatMap((observationType) =>
                      observationType.code &&
                      observationType.display &&
                      observationType.system
                        ? [
                            defaultToOption({
                              code: observationType.code,
                              display: observationType.display,
                              system: observationType.system,
                            }),
                          ]
                        : []
                    )
                  : undefined
              }
            >
              <FacesheetCardAddButton aria-label="Add observation" />
            </ComboboxAsync>
          )}
        </FacesheetCardActions>
      </FacesheetCardHeader>
      {facesheetImprovements && (
        <FacesheetCardContent>
          <FacesheetCardSections>
            {/* Rows with no data render nothing, so the heading goes too
                once none are left. */}
            <FacesheetCardSection
              title="General"
              className="[&:not(:has([data-observation-row]))]:hidden"
            >
              {GENERAL_OBSERVATIONS.map((item) => (
                <ObservationRow
                  key={item.label}
                  item={item}
                  participant={participant}
                  section="general"
                  hideWhenEmpty
                />
              ))}
            </FacesheetCardSection>
            {showRecent && !!recentRows.length && (
              <FacesheetCardSection title="Recent">
                {recentRows.slice(0, RECENT_LIMIT).map((obs) => (
                  <RecentObservationRow
                    key={obs.id}
                    observation={obs}
                    participant={participant}
                  />
                ))}
                {!!observationCount && (
                  <TextLink
                    to={`${participant.relativeUrl}/observations`}
                    className="self-start text-xs text-stone-500"
                    onClick={() =>
                      reportFacesheetObservationsSeeAllClicked({
                        participantId: participant.id,
                        tab: variant,
                        count: observationCount,
                      })
                    }
                  >
                    See all {observationCount}
                  </TextLink>
                )}
              </FacesheetCardSection>
            )}
            {showPins && !!additionalObservationTypes.length && (
              <FacesheetCardSection title="Pinned">
                <ReorderList
                  items={additionalObservationTypes.map((value) => ({
                    id: reorderId(value),
                    component: (
                      <ObservationRow
                        item={{
                          label: value.display ?? value.code ?? '',
                          codings: [value],
                        }}
                        participant={participant}
                        section="pinned"
                        onRemove={
                          isReadOnly
                            ? undefined
                            : () => handleRemoveObservation(value, 'row')
                        }
                      />
                    ),
                  }))}
                  onChange={isReadOnly ? () => {} : handleReorder}
                  className={FACESHEET_CARD_LIST_CLASS}
                  dragHandle={!isReadOnly}
                  // The handle fills the 12px gutter between the row and the
                  // card edge and covers nothing else, so it takes the pointer
                  // while hidden and hovering the gutter reveals it. The chip
                  // fits the gutter, so its border is an inset ring in the
                  // + button's border colors; a real border would shrink the
                  // grip. The dots take the card text color, as the x does.
                  dragHandleClassName="z-10 px-0 focus-visible:opacity-100 [&>svg]:h-4 [&>svg]:w-3 [&>svg]:shrink-0 [&>svg]:rounded-xs [&>svg]:bg-white [&>svg]:py-0.5 [&>svg]:text-current [&>svg]:shadow-xs [&>svg]:ring-1 [&>svg]:ring-inset [&>svg]:ring-zinc-200 dark:[&>svg]:bg-stone-900 dark:[&>svg]:ring-zinc-800"
                />
              </FacesheetCardSection>
            )}
          </FacesheetCardSections>
        </FacesheetCardContent>
      )}
      {!facesheetImprovements && hasObservations && (
        <FacesheetCardContent>
          <FacesheetCardSections>
            {!!pinnedObservationTypes.length && (
              <ReorderList
                items={pinnedObservationTypes.map((value) => ({
                  id: reorderId(value),
                  component: (
                    <ObservationCard
                      coding={value}
                      participant={participant}
                      onRemove={
                        isReadOnly
                          ? undefined
                          : () => handleRemoveObservation(value, 'row')
                      }
                    />
                  ),
                }))}
                onChange={isReadOnly ? () => {} : handleReorder}
                className={FACESHEET_CARD_LIST_CLASS}
                dragHandle={!isReadOnly}
              />
            )}
            {!!fallbackObservations.length && (
              <FacesheetCardSection
                title={
                  relevantObservations?.values?.length
                    ? 'Other recent observations'
                    : 'Recent observations'
                }
              >
                {fallbackObservations.map((obs) => {
                  const coding = obs.codeableConcept.coding?.[0]
                  if (!coding?.code || !coding?.display || !coding?.system) {
                    return null
                  }
                  const typedCoding = {
                    code: coding.code,
                    display: coding.display,
                    system: coding.system,
                  }
                  return (
                    <ObservationCard
                      key={`${coding.system}-${coding.code}`}
                      coding={coding}
                      participant={participant}
                      onAdd={
                        isReadOnly
                          ? undefined
                          : () => handleAddObservation(typedCoding)
                      }
                    />
                  )
                })}
              </FacesheetCardSection>
            )}
          </FacesheetCardSections>
        </FacesheetCardContent>
      )}
    </FacesheetCard>
  )
}

function sameCoding(a: Coding | undefined, b: Coding): boolean {
  return a?.system === b.system && a?.code === b.code
}

function reorderId(observationType: Coding): string {
  return `${observationType.system}-${observationType.code}`
}

function ObservationCard({
  coding,
  participant,
  onRemove,
  onAdd,
}: {
  coding: Coding
  participant: Participant
  onRemove?: () => void
  onAdd?: () => void
}): ReactNode {
  const observationsQuery = useSearch(ExtendedObservation, {
    _sort: '-date',
    subject: participant.reference,
    code: coding,
  })

  const latestObservation = observationsQuery.data?.find(
    (obs) => !obs.isEnteredInError
  )
  // A multi-select answer is several records: the line shows all its values.
  const latestCapture = groupObservationCaptures(
    (observationsQuery.data ?? []).filter((obs) => !obs.isEnteredInError)
  )[0]
  const capturedValues =
    latestCapture && latestCapture.members.length > 1
      ? latestCapture.values.join(', ')
      : undefined
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const adaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const rowRef = useRef<HTMLDivElement>(null)
  const {
    side: adaptiveSide,
    sideOffset: adaptiveSideOffset,
    onOpenChange,
    cardRef,
  } = useAdaptiveHoverCardPlacement(rowRef, OBSERVATION_HOVER_CARD_WIDTH)

  if (observationsQuery.isLoading) {
    return <Skeleton className="h-4 w-full" />
  }

  return (
    <HoverCard
      openDelay={200}
      closeDelay={100}
      onOpenChange={adaptivePlacement ? onOpenChange : undefined}
    >
      {/* The whole row is the trigger, as it has always been. Under
          side="bottom" Radix anchors to the trigger's left edge, which the row
          and the label share, so keeping the row as the trigger costs the
          viewport layout nothing and leaves the flag-off hover target intact.
          Adaptive placement additionally reads the row's right edge, which is
          the content column's, to decide whether a right-side card fits. */}
      <HoverCardTrigger asChild>
        <div
          ref={rowRef}
          key={`${coding.system}-${coding.code}`}
          className="group/observation relative"
        >
          <p className={FACESHEET_CARD_ROW_CLASS}>
            {latestObservation ? (
              <TextLink
                to={`${participant.relativeUrl}/observations/${latestObservation.id}`}
              >
                {coding.display}
                <span className="font-semibold">
                  {': '}
                  {/* A code with a template reads as every other surface
                      shows it; dates and reference answers have no value
                      the Medplum formatters read. */}
                  {capturedValues
                    ? capturedValues
                    : latestObservation.template
                      ? latestObservation.valueDisplay
                      : formatObservationValueDisplay(
                          latestObservation.toFhirResource()
                        ) || latestObservation.valueDisplay}
                </span>
              </TextLink>
            ) : (
              <span>{coding.display}</span>
            )}
            {latestObservation && (
              <span className="select-none">
                {latestObservation.effectiveDateTime && (
                  <>
                    <DotSeparator />
                    <span className="text-stone-500">
                      {facesheetImprovements
                        ? formatDateNumberShort(
                            latestObservation.effectiveDateTime
                          )
                        : formatDateTime(latestObservation.effectiveDateTime)}
                    </span>
                  </>
                )}
              </span>
            )}
            {!latestObservation && (
              <span className="select-none">
                <DotSeparator />
                <span className="text-stone-500">No data</span>
              </span>
            )}
          </p>
          {onRemove && (
            <Button
              size="unstyled"
              variant="unstyled"
              onClick={onRemove}
              aria-label="Remove observation"
              className="absolute! top-0 right-0 flex h-4 w-4 items-center justify-center p-0 opacity-0 group-hover/observation:opacity-100"
            >
              <IconX className="size-3" />
            </Button>
          )}
          {onAdd && (
            <Button
              size="unstyled"
              variant="unstyled"
              onClick={onAdd}
              aria-label="Add observation"
              className="absolute! top-0 right-0 flex h-4 w-4 items-center justify-center p-0 opacity-0 group-hover/observation:opacity-100"
            >
              <IconPlus className="size-3" />
            </Button>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        ref={adaptivePlacement ? cardRef : undefined}
        side={
          adaptivePlacement ? adaptiveSide : viewportLayout ? 'bottom' : 'right'
        }
        sideOffset={adaptivePlacement ? adaptiveSideOffset : undefined}
        align="start"
        alignOffset={viewportLayout || adaptivePlacement ? undefined : -16}
        scrollChainAnchor={adaptivePlacement ? rowRef : undefined}
        className="w-lg overflow-visible"
      >
        <ObservationHistoryChart
          subject={participant.reference}
          coding={coding}
          observation={latestObservation}
        />
      </HoverCardContent>
    </HoverCard>
  )
}

// One General or Pinned line: the item's latest value as a link, then its
// date.
function ObservationRow({
  item,
  participant,
  section,
  onRemove,
  hideWhenEmpty = false,
}: {
  item: GeneralObservation
  participant: Participant
  section: Exclude<FacesheetObservationSection, 'recent'>
  onRemove?: () => void
  // General rows with nothing recorded are left out; a pinned row still says
  // "No data".
  hideWhenEmpty?: boolean
}): ReactNode {
  const { data: observation, isLoading } = useSearchOne(ExtendedObservation, {
    _sort: '-date',
    subject: participant.reference,
    code: item.codings,
    'status:not': 'entered-in-error',
  })
  const rowRef = useRef<HTMLDivElement>(null)

  if (isLoading) {
    return <Skeleton data-observation-row className="h-4 w-full" />
  }

  if (!observation && hideWhenEmpty) {
    return null
  }

  const dateLabel = observation
    ? observation.effectiveDateTime &&
      formatDateNumberShort(observation.effectiveDateTime)
    : 'No data'

  return (
    <div
      ref={rowRef}
      data-observation-row
      className="group/observation relative"
    >
      <p className={FACESHEET_CARD_ROW_CLASS}>
        {observation ? (
          <ObservationHoverLink
            item={item}
            observation={observation}
            participant={participant}
            rowRef={rowRef}
            section={section}
          />
        ) : (
          item.label
        )}
        {dateLabel && (
          <span className="select-none">
            <DotSeparator />
            <span className="text-stone-500">{dateLabel}</span>
          </span>
        )}
      </p>
      {onRemove && (
        // Hidden controls take no clicks until the row is hovered or the
        // button is focused.
        <Button
          size="unstyled"
          variant="unstyled"
          onClick={onRemove}
          aria-label="Remove observation"
          className="pointer-events-none absolute! top-1/2 right-0 flex size-[18px] -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white p-0 opacity-0 shadow-xs group-hover/observation:pointer-events-auto group-hover/observation:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 dark:border-zinc-800 dark:bg-stone-900"
        >
          <IconX className="size-3" />
        </Button>
      )}
    </div>
  )
}

// A Highlights Recent line: read-only, from the observation already fetched.
function RecentObservationRow({
  observation,
  participant,
}: {
  observation: ExtendedObservation
  participant: Participant
}): ReactNode {
  const rowRef = useRef<HTMLDivElement>(null)
  const coding = observation.codeableConcept.coding?.[0] ?? {}
  return (
    <div ref={rowRef} data-observation-row>
      <p className={FACESHEET_CARD_ROW_CLASS}>
        <ObservationHoverLink
          item={{
            label: observation.display ?? coding.code ?? 'Observation',
            codings: [coding],
          }}
          observation={observation}
          participant={participant}
          rowRef={rowRef}
          section="recent"
        />
        {observation.effectiveDateTime && (
          <span className="select-none">
            <DotSeparator />
            <span className="text-stone-500">
              {formatDateNumberShort(observation.effectiveDateTime)}
            </span>
          </span>
        )}
      </p>
    </div>
  )
}

// The underlined link is the hover target, but the trend card is placed as
// ConditionsCard places its card against the whole row. Radix anchors to the
// trigger, so on open the gap between the link and the row's edges is added to
// the offsets.
function ObservationHoverLink({
  item,
  observation,
  participant,
  rowRef,
  section,
}: {
  item: GeneralObservation
  observation: ExtendedObservation
  participant: Participant
  rowRef: RefObject<HTMLDivElement | null>
  section: FacesheetObservationSection
}): ReactNode {
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const facesheetTab = useFacesheetTab()
  const adaptivePlacement = useFeatureFlag(
    featureFlags.hoverCardAdaptivePlacement
  )
  const viewportLayout = useFeatureFlag(featureFlags.hoverCardViewportLayout)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const {
    side: adaptiveSide,
    sideOffset: adaptiveSideOffset,
    onOpenChange: onAdaptiveOpenChange,
    cardRef,
  } = useAdaptiveHoverCardPlacement(rowRef, OBSERVATION_HOVER_CARD_WIDTH)
  const [rowShift, setRowShift] = useState({ side: 0, align: 0 })
  const coding = generalObservationCoding(
    item,
    observation.codeableConcept.coding
  )
  const side = adaptivePlacement
    ? adaptiveSide
    : viewportLayout
      ? 'bottom'
      : 'right'

  const handleOpenChange = (open: boolean): void => {
    const row = rowRef.current?.getBoundingClientRect()
    const link = linkRef.current?.getBoundingClientRect()
    if (open && row && link) {
      setRowShift(
        side === 'bottom'
          ? { side: row.bottom - link.bottom, align: row.left - link.left }
          : { side: row.right - link.right, align: row.top - link.top }
      )
    }
    if (adaptivePlacement) {
      onAdaptiveOpenChange(open)
    }
    if (open && facesheetImprovements) {
      reportFacesheetObservationTrendOpened({
        participantId: participant.id,
        tab: facesheetTab?.tab,
        section,
      })
    }
  }

  return (
    <HoverCard openDelay={200} closeDelay={100} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        <TextLink
          ref={linkRef}
          to={`${participant.relativeUrl}/observations/${observation.id}`}
        >
          {item.label}
          <span className="font-semibold">
            {': '}
            {formatObservationValueDisplay(observation.toFhirResource())}
          </span>
        </TextLink>
      </HoverCardTrigger>
      <HoverCardContent
        ref={adaptivePlacement ? cardRef : undefined}
        side={side}
        sideOffset={
          (adaptivePlacement ? adaptiveSideOffset : HOVER_CARD_SIDE_OFFSET) +
          rowShift.side
        }
        align="start"
        alignOffset={rowShift.align}
        scrollChainAnchor={adaptivePlacement ? rowRef : undefined}
        className="w-[32rem] overflow-visible"
      >
        <ObservationHistoryChart
          subject={participant.reference}
          coding={coding}
          observation={observation}
        />
      </HoverCardContent>
    </HoverCard>
  )
}
